import { renovarAccessToken } from './renovacion'
import { tokenStore } from './tokenStore'

/**
 * Adopción de la sesión de plataforma (política de auth §5.6 del CLAUDE.md
 * raíz): una sesión válida en una web SSO vale en todas. Si no hay access
 * token en memoria, se intenta UN refresh silencioso contra la cookie
 * httpOnly del gateway (`Domain=.tecopos.com`); si la cookie revive el token,
 * el usuario entra sin volver a pasar por /login. El visitante anónimo paga
 * un único intento fallido y sigue como anónimo.
 *
 * Semántica:
 * - Single-flight: llamadas concurrentes (StrictMode incluido) comparten la
 *   misma promesa.
 * - Una sola sonda por carga de página: agotada la sonda, las llamadas
 *   posteriores devuelven lo que haya en el tokenStore EN ESE MOMENTO —
 *   nunca el resultado viejo de la sonda, que podría ser un token de una
 *   sesión ya revocada (evita un loop /login ↔ /app con sesión muerta).
 * - **NO deriva al login**: la sonda corre también en páginas públicas, así que
 *   los desenlaces `sesion-invalida` y `red` del módulo canónico se tratan
 *   igual aquí — `null`, o sea visitante. Quien decide qué hacer con un
 *   visitante es la pantalla (RequireAuth, el gate de la landing…), no la sonda.
 *   Por eso llama a `renovarAccessToken` (transporte) y no a
 *   `renovarOCerrarSesion` (política de web privada).
 */
let probe: Promise<string | null> | null = null
let probeDone = false

/** Solo para tests: repermite el intento de adopción. */
export function resetSessionAdoption(): void {
  probe = null
  probeDone = false
}

export function adoptPlatformSession(): Promise<string | null> {
  const current = tokenStore.get()
  if (current) return Promise.resolve(current)
  if (probeDone) return Promise.resolve(tokenStore.get())
  probe ??= (async () => {
    try {
      const desenlace = await renovarAccessToken()
      // `renovarAccessToken` ya guardó el token en el store si revivió.
      return desenlace.tipo === 'renovado' ? desenlace.token : null
    } catch {
      // Entorno sin `VITE_GATEWAY_URL` o cualquier fallo inesperado: anónimo.
      return null
    } finally {
      probeDone = true
    }
  })()
  return probe
}
